// v20230312 - mix from printr and bosques + auth verify

import { inject, Inject, Injectable, Optional } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  Auth, authState, idToken, signOut, User, signInAnonymously,
  signInWithEmailAndPassword, updateProfile, createUserWithEmailAndPassword, applyActionCode, verifyPasswordResetCode, confirmPasswordReset, getIdToken, updateCurrentUser,
} from '@angular/fire/auth';
import { doc, Firestore, fromRef } from '@angular/fire/firestore';
import { intersection, isEqual } from 'lodash';
import { NgxRolesService } from 'ngx-permissions';
import { BehaviorSubject, NEVER, Subscription, } from 'rxjs';
import { distinctUntilChanged, distinctUntilKeyChanged, filter, map, skip, switchMap } from 'rxjs/operators';
import { UserDbService } from 'projects/shared-lib/src/database/user-db-service';
import { UserModel } from 'projects/shared-lib/src/models/user.model';
import { UserApi } from 'projects/shared-lib/src/a2-module-common/api-server/user-api';
import { ROLE_RULES } from 'projects/shared-lib/src/core/permissions/role-rule';
import { Roles, RolesAdministrative } from 'projects/shared-lib/src/core/permissions/role-def';


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _user = new BehaviorSubject<User | null>(null);
  public user$ = this._user.asObservable();
  roles: Array<Roles> = [];// RolesKey;
  refreshTokenSubscription: Subscription | undefined;
  static readonly AllowedRoles = RolesAdministrative;
  private auth = inject(Auth);
  private firestore= inject(Firestore);

  constructor(
    // @Optional() private auth: Auth,
    // @Optional() private firestore: Firestore,
    private userDbService: UserDbService,
    private rolesService: NgxRolesService,
    private userApi: UserApi,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.init()
  }

  async isUserAllowed(claims = undefined) {
    const _claims = claims ?? (await this.auth?.currentUser?.getIdTokenResult())?.claims;
    const roles = _claims?.roles ?? [];
    return intersection(roles, AuthService.AllowedRoles).length > 0;
  }

  init() {
    // console.log("//////////// INICIO AUTH INIT //////////////")
    idToken(this.auth).pipe(distinctUntilChanged()).subscribe(async idToken => {
      const tokenResult = await this.auth.currentUser?.getIdTokenResult();
      // console.log(`====>>> idToken:`, idToken);
      // console.log("tokenResult.claims", tokenResult.claims)
      // console.log('=====>>> tokenResult', tokenResult);
      const roles = (tokenResult?.claims?.['roles'] ?? []) as Array<UserModel.Role>;
      // PATCH: kick out if not allowed
      if (this.auth.currentUser) {
        const isAllowed = await this.isUserAllowed(tokenResult?.claims);
        if (!isAllowed) {
          // console.log('Not allowed here, kick out!');
          await this.signOut();
          return;
        }
      }
      const isDiferent = !isEqual(roles, this.roles);
      // console.log(`===>> isDiferent A: ***${roles}***`);
      // console.log(`===>> isDiferent B: ***${this.roles}***`);
      // console.log('got roles:', this.roles, roles, isDiferent);
      this.roles = [...roles];
      // console.log(`===>> Current roles: ***${this.roles}***`);
      if (isDiferent) {
        this.setRoles({ roles, user: this.auth.currentUser });
      }
      // last: notify of user
      this._user.next(this.auth.currentUser);
    })

    // authState(this.auth).pipe(distinctUntilChanged()).subscribe(async (user: User) => {
    //   // console.log('auth state:', user);
    //   // this._user.next(user);
    //   if (!user) {

    //     // console.log("despues...")
    //     //PATCH JA 12/2021 this.router.navigate(['signin']);
    //     localStorage.setItem('user', 'null');
    //     // JSON.parse(localStorage.getItem('user')!);
    //     // await this.createAnonymousUser();
    //   } else {
    //     this.userData = user;
    //     localStorage.setItem('user', JSON.stringify(this.userData));
    //     //JSON.parse(localStorage.getItem('user')!);
    //     // this.isInit += 1;
    //   }
    // })

    this.refreshTokenSubscription = this.tokenChanged$.pipe(skip(1)).subscribe(async t => {
      if (this.auth.currentUser) {
        await this.auth.currentUser.reload(); //TODO se debería eliminar y tomar datos del token ==> MN: ver commentario abajo
        // ==> MN: esto hace force del refresh token. con el cambio de arriba en idToken() ya funciona bien cambiar de roles/usuario (aunque la versión del servidor de bosques no actualiza el meta/refreshToken)
        await this.auth.currentUser.getIdTokenResult(true);
      }
    })
  }

  tokenChanged$ = authState(this.auth).pipe(
    filter(user => !!user?.uid),
    distinctUntilKeyChanged('uid'),
    switchMap(user => {
      return user && user.uid
        ? fromRef(doc(this.firestore, `users/${user.uid}/meta/refreshToken`)).pipe(map(snap => snap.data()))
        : NEVER
    })
  )

  setRoles(authProp: { roles: Array<string>; user: User | null; }) {
    this.rolesService.flushRolesAndPermissions();
    if (authProp.roles) {
      // console.log(authProp);
      //.
      this.roles = authProp.roles as Array<Roles>
      const rolesPermissions = ROLE_RULES.reduce((acc, roleRule) => {
        // console.log(this.roles,"R",{acc, roleRule});
        if (authProp.roles.includes(roleRule.role)) {
          acc[roleRule.role] = roleRule.permissions;
        }
        return acc;
      }, {})
      // console.log("rolesPermissions", rolesPermissions)
      this.rolesService.addRolesWithPermissions(rolesPermissions);
    }
  }

  //Signin Anonymous
  createAnonymousUser(): Promise<any> {
    return signInAnonymously(this.auth);
  }

  // Sign in with email/password
  signIn(email: string, password: string) {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  // Sign up with email/password
  signUp(email: string, password: string, data: {}) {
    return createUserWithEmailAndPassword(this.auth, email, password)
      .then((result) => {
        this.sendVerificationMail(data);
        return this.setUserData(result.user, data);
      })
      .catch((error) => {
        window.alert(error.message);
      });
  }

  // Send email verfificaiton when new user sign up
  sendVerificationMail(email, _url?) {
    const url = _url ?? `${this.document.location.origin}`;
    var actionCodeSettings = { url }
    return this.userApi.emailVerify({ email, actionCodeSettings });
  }

  // Reset Forggot password
  forgotPassword(passwordResetEmail: string, _url?) {
    const url = _url ?? `${this.document.location.origin}`;
    var actionCodeSettings = { url };
    return this.userApi.passwordRecovery({ email: passwordResetEmail, actionCodeSettings })
  }
  // Returns true when user is looged in and email is verified
  // NOP: in new stuff don't use local storage!
  // get isLoggedIn(): boolean {
  //   const user = JSON.parse(localStorage.getItem('user')!);
  //   return user !== null && user.emailVerified !== false ? true : false;
  // }

  /* Setting up user data when sign in with username/password,
  sign up with username/password and sign in with social auth
  provider in Firestore database using AngularFirestore + AngularFirestoreDocument service */
  async setUserData(user: any, data) {

    const userData: any = {
      id: user.uid,
      uid: user.uid,
      email: user.email,
      displayName: user.displayName ?? data.displayName,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
      ...data
    };
    await updateProfile(user, { displayName: userData.displayName });
    // console.log("Set user a guardar ", userData)
    return this.userDbService.upsert(userData, {isNew:true})
  }
  // Sign out
  signOut() {
    // console.log('SIGN OUT');
    // this._user.next(null);
    return this.auth
      ? signOut(this.auth)
      : Promise.resolve(false)
  }

  public applyActionCode(actionCode) {
    return applyActionCode(this.auth, actionCode)
  }

  public verifyPasswordResetCode(actionCode) {
    return verifyPasswordResetCode(this.auth, actionCode);
  }

  public confirmPasswordReset(actionCode, newPassword) {
    return confirmPasswordReset(this.auth, actionCode, newPassword);
  }

  public async resetToken() {
    await getIdToken(this.auth.currentUser, true);
    await updateCurrentUser(this.auth, this.auth.currentUser);
    const user = this.auth.currentUser;
    setTimeout(() => {
      user.reload();
    }, 200);
    return
  }

  async updateUserData(data) {
    if (data.displayName) {
      await updateProfile(this.auth.currentUser, { displayName: data.displayName });
      await getIdToken(this.auth.currentUser, true);
    }
    return this.userDbService.upsert({ id: this.auth.currentUser.uid, ...data })
  }
}
